/**
 * bugs — the PURE rules of the forest's insect swarm (2026-09-11, "bug
 * particles that can interact with your user in a forest").
 *
 * One swarm lives around the viewer inside the woods: GNATS by day (a hovering
 * cloud that drifts to the viewer's head when they stand still and scatters
 * when they move or swing), MOTHS at dusk, FIREFLIES by night (pulsing
 * emissive points that drift low and slow). Every bug wanders on noise
 * around a drifting swarm centre, keeps out of a personal bubble around the
 * viewer (and any other person), and is blown by the wind.
 */

export type BugKind = 'gnat' | 'moth' | 'firefly';

export interface BugRules {
  count: number;
  /** Swarm radius around its centre (m) and the centre's offset height over the ground. */
  radius: number;
  height: number;
  /** Speed band m/s; jitter (m/s² of noise acceleration). */
  speed: [number, number];
  jitter: number;
  /** Size of the sprite (m). */
  size: [number, number];
  /** The bug keeps out of a bubble this wide around a person (m); scatters at this speed. */
  bubble: number;
  scatter: number;
  /** Emissive (fireflies) pulse period (s), 0 = not emissive. */
  pulse: number;
}

export const BUG_RULES: Record<BugKind, BugRules> = {
  gnat:    { count: 110, radius: 4.5, height: 1.5, speed: [0.4, 1.4], jitter: 6, size: [0.02, 0.035], bubble: 1.4, scatter: 3.5, pulse: 0 },
  moth:    { count: 50,  radius: 6,   height: 2.2, speed: [0.6, 1.8], jitter: 4, size: [0.05, 0.09], bubble: 1.8, scatter: 2.5, pulse: 0 },
  firefly: { count: 70,  radius: 9,   height: 1.2, speed: [0.2, 0.7], jitter: 1.5, size: [0.05, 0.08], bubble: 1.6, scatter: 2, pulse: 2.6 },
};

/** Forest density from which a swarm forms; rain or snow above this intensity grounds it. */
export const BUG_FOREST_MIN = 0.35;
export const BUG_RAIN_MAX = 0.4;
/** The viewer standing still this long draws the gnats to their head. */
export const BUG_SETTLE_S = 3;

/** Which swarm the hour calls for (night01 from the sun): gnats by day, moths at dusk, fireflies at night. */
export function bugKindFor(night01: number, temperatureC: number): BugKind | null {
  if (temperatureC < 4) return null;        // too cold for insects
  if (night01 < 0.25) return 'gnat';
  if (night01 < 0.7) return 'moth';
  return 'firefly';
}

/** Whether a swarm forms here. */
export function swarmAllowed(forestDensity: number, precip: number, indoors: boolean, temperatureC: number): boolean {
  return !indoors && forestDensity >= BUG_FOREST_MIN && precip <= BUG_RAIN_MAX && temperatureC >= 4;
}

export interface BugState {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  /** Own phase (pulse, wing noise). */
  phase: number;
  size: number;
}

export interface Person { x: number; y: number; z: number; still: number }

/**
 * One bug's step: noise wander + a pull toward the swarm centre (stronger
 * outside the radius) + a bubble around every person (pushed out at the
 * scatter speed) + the wind + a vertical band. Gnats also DRIFT to a still
 * viewer's head (they hover at 0.3–1.0 m around it).
 */
export function stepBug(b: BugState, r: BugRules, kind: BugKind, cx: number, cy: number, cz: number, people: Person[], windX: number, windZ: number, wind01: number, dt: number, rng: () => number): void {
  // Wander.
  b.vx += (rng() - 0.5) * r.jitter * dt;
  b.vy += (rng() - 0.5) * r.jitter * 0.5 * dt;
  b.vz += (rng() - 0.5) * r.jitter * dt;
  // Cohesion: the pull grows past the radius.
  let dx = cx - b.x, dz = cz - b.z;
  const d = Math.hypot(dx, dz);
  if (d > r.radius * 0.6) { const k = ((d - r.radius * 0.6) / r.radius) * 3; b.vx += (dx / (d || 1)) * k * dt; b.vz += (dz / (d || 1)) * k * dt; }
  // Height band.
  b.vy += (cy - b.y) * 1.2 * dt;
  // People: keep out of the bubble, scatter; a still viewer draws the gnats in.
  for (const p of people) {
    const px = b.x - p.x, pz = b.z - p.z, py = b.y - (p.y + 1.5);
    const pd = Math.hypot(px, py, pz);
    if (pd < r.bubble) {
      const k = (r.bubble - pd) / r.bubble;
      b.vx += (px / (pd || 1)) * r.scatter * k;
      b.vy += (py / (pd || 1)) * r.scatter * k * 0.5;
      b.vz += (pz / (pd || 1)) * r.scatter * k;
    } else if (kind === 'gnat' && p.still > BUG_SETTLE_S && pd < r.radius * 1.5) {
      // Drift toward the head (they stop at the bubble).
      b.vx -= (px / pd) * 0.8 * dt; b.vy -= (py / pd) * 0.8 * dt; b.vz -= (pz / pd) * 0.8 * dt;
    }
  }
  // Wind.
  b.vx += windX * wind01 * 1.5 * dt; b.vz += windZ * wind01 * 1.5 * dt;
  // Speed band.
  const s = Math.hypot(b.vx, b.vy, b.vz);
  let nearPerson = false;
  for (let i = 0; i < people.length; i++) { const p = people[i]; if (Math.hypot(b.x - p.x, b.z - p.z) < r.bubble * 1.5) { nearPerson = true; break; } }
  const cap = r.speed[1] * (nearPerson ? 2.5 : 1);
  if (s > cap) { const k = cap / s; b.vx *= k; b.vy *= k; b.vz *= k; }
  else if (s < r.speed[0]) { const k = r.speed[0] / (s || 1); b.vx *= k; b.vy *= k; b.vz *= k; }
  b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
  b.phase += dt;
}

/** Firefly glow 0..1 at phase t (a soft double blink every `pulse` s). */
export function fireflyGlow(t: number, pulse: number): number {
  if (pulse <= 0) return 0;
  const u = (t % pulse) / pulse;
  const a = Math.max(0, 1 - Math.abs(u - 0.18) / 0.12);
  const b = Math.max(0, 1 - Math.abs(u - 0.36) / 0.1) * 0.7;
  return Math.min(1, a + b) * 0.9 + 0.05;
}

/** The swarm centre follows the viewer with a lag and a wind lean. */
export function swarmCentreStep(cx: number, cz: number, vx: number, vz: number, windX: number, windZ: number, wind01: number, dt: number): { x: number; z: number } {
  const tx = vx + windX * wind01 * 2, tz = vz + windZ * wind01 * 2;
  const k = Math.min(1, dt * 0.8);
  return { x: cx + (tx - cx) * k, z: cz + (tz - cz) * k };
}
